<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import { ACTION_TYPES, CONTENT_TYPES, LOCKED_LINK_MESSAGE, actionPermissions } from '@/util/access_visibility'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/tooltip'
import OpensInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import type { ResearchCitation } from '@/strategies/strategy_types'

defineProps<{ researchCitations: ResearchCitation[] }>()

const analytics = getAnalyticsTracker()
const { authInfo } = storeToRefs(useUserStore())

const shouldBlockLink = computed(() =>
  actionPermissions(ACTION_TYPES.FOLLOW_LINK, CONTENT_TYPES.STRATEGY, {}, authInfo.value).block
)

function clickedResearchLink(researchCitation: ResearchCitation) {
  analytics.sendEvent(EVENT_NAMES.RESEARCH_CITATIONS.CLICKED, {
    research_citation_uuid: researchCitation.uuid,
    research_citation_title: researchCitation.title
  })
}
</script>

<template>
  <div class="text-gray-900">
    <h2 class="mb-2.5 mt-5 text-display-md font-bold leading-[1.1]">Research Citation Links</h2>
    <ul class="list-none px-6 text-lg leading-[25px]">
      <li
        v-for="researchCitation in researchCitations"
        :key="researchCitation.uuid"
        class="mb-2.5 ml-9 -indent-9 [overflow-wrap:anywhere]"
      >
        <TooltipProvider v-if="shouldBlockLink" :delay-duration="0">
          <Tooltip>
            <TooltipTrigger as-child>
              <button type="button" class="text-left text-blue-600" aria-disabled="true">{{ researchCitation.citation }}</button>
            </TooltipTrigger>
            <TooltipContent>{{ LOCKED_LINK_MESSAGE }}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <a
          v-else
          :href="researchCitation.source_link"
          target="_blank"
          class="text-blue-600 hover:underline"
          @click="clickedResearchLink(researchCitation)"
          >{{ researchCitation.citation }}<OpensInNewTabIcon size="sm" class="ml-1 indent-0"
        /></a>
      </li>
    </ul>
  </div>
</template>
